import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { CLASS_DESCRIPTIONS } from '../data/athletes';

const GROUP_LABELS: Record<string, string> = {
  AL: 'Lower Limb Impairment',
  AU: 'Upper Limb Impairment',
  RP: 'Range & Power Impairment',
  B:  'Visual Impairment',
};

interface Props {
  classId: string;
  /** Visual style: 'inline' (small grey text, default), 'large' (big heading style), or 'on-dark' (white text for dark backgrounds) */
  variant?: 'inline' | 'large' | 'on-dark';
}

const ClassBadge: React.FC<Props> = ({ classId, variant = 'inline' }) => {
  const [open, setOpen] = useState(false);
  const description = CLASS_DESCRIPTIONS[classId];
  const groupKey = Object.keys(GROUP_LABELS).find(k => classId.startsWith(k)) ?? '';
  const groupLabel = GROUP_LABELS[groupKey] ?? '';

  const badge =
    variant === 'large' ? (
      <button
        onClick={() => setOpen(true)}
        title="View class description"
        className="text-3xl font-bold underline decoration-dotted decoration-blue-300 cursor-pointer hover:text-blue-300 transition-colors"
      >
        {classId}
      </button>
    ) : variant === 'on-dark' ? (
      <button
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        title="View class description"
        className="underline decoration-dotted decoration-white/50 cursor-pointer hover:text-white transition-colors"
      >
        {classId}
      </button>
    ) : (
      <button
        onClick={e => { e.stopPropagation(); setOpen(true); }}
        title="View class description"
        className="underline decoration-dotted decoration-gray-400 cursor-pointer hover:text-secondary transition-colors"
      >
        {classId}
      </button>
    );

  return (
    <>
      {badge}

      {open && ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className="text-3xl font-extrabold text-dark">{classId}</span>
                {groupLabel && <p className="text-xs text-gray-400 mt-0.5 uppercase tracking-wide">{groupLabel}</p>}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-4"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed">
              {description ?? 'No description available for this class.'}
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ClassBadge;
